import {useCallback, useEffect, useSyncExternalStore} from 'react';
import {auth} from '../../firebase';
import {
  clearGuidedStartPending,
  fetchUniversityCatalog,
  getGuidedStartPendingId,
  reportGettingStartedDismissed,
  reportGuidedStart,
  reportGuidedWelcomeSeen,
  reportVideoProgress,
  type UniversityCatalogPayload,
  type UniversityVideo,
} from '../../services/universityService';
import {captureUniversity} from './universityAnalytics';

// ─── Shared Opalite University store ─────────────────────────────────────────
//
// One module-level copy of the catalog + progress shared by every surface
// (the /learn library, the contextual tips, the guided welcome modal, the
// Getting Started card). A per-component fetch would refire on every route
// change because AppShell remounts per route; a context provider inside
// AppShell would reset the same way. Mutations update the store optimistically
// and sync to the backend best-effort — education state is never worth
// blocking the UI over.

export interface UniversityState extends UniversityCatalogPayload {
  status: 'idle' | 'loading' | 'ready' | 'error';
}

const EMPTY_STATE: UniversityState = {
  status: 'idle',
  categories: [],
  videos: [],
  playlist: [],
  progress: {},
  guided: {
    active: false,
    showGuidedWelcome: false,
    showGettingStartedCard: false,
    playlistWatchedCount: 0,
    playlistTotal: 0,
  },
};

let state: UniversityState = EMPTY_STATE;
let loadedForUid: string | null = null;
let inFlight: Promise<void> | null = null;
let activeGuidedStartId: string | null = null;
const listeners = new Set<() => void>();

function setState(next: UniversityState) {
  state = next;
  listeners.forEach(listener => listener());
}

function getState(): UniversityState {
  return state;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const swallow = (label: string) => (err: unknown) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn(`[university] ${label} failed:`, err);
  }
};

async function loadCatalog(uid: string): Promise<void> {
  setState({...state, status: state.status === 'ready' ? 'ready' : 'loading'});
  try {
    const payload = await fetchUniversityCatalog();
    loadedForUid = uid;
    setState({status: 'ready', ...payload});
  } catch (err) {
    swallow('catalog load')(err);
    loadedForUid = uid;
    setState({...EMPTY_STATE, status: 'error'});
  }
}

function ensureLoaded(): void {
  const uid = auth.currentUser?.uid || null;
  if (!uid) return;
  const guidedStartId = getGuidedStartPendingId();
  if (!guidedStartId && loadedForUid === uid && state.status !== 'idle') return;
  if (inFlight) return;
  if (loadedForUid !== uid) {
    // Account switch on the same device: never leak the previous user's
    // progress into the new session.
    state = EMPTY_STATE;
    loadedForUid = null;
    if (!guidedStartId) activeGuidedStartId = null;
  }
  inFlight = (async () => {
    let resetSucceeded = false;
    // The unique id makes this safe to retry after a refresh: the backend
    // performs each /start reset once, then returns success without touching
    // any progress recorded after that reset.
    if (guidedStartId) {
      try {
        await reportGuidedStart(guidedStartId);
        resetSucceeded = true;
      } catch (err) {
        // Leave the flag in place so the next app load retries the handoff.
        swallow('guided-start handoff')(err);
      }
    }
    await loadCatalog(uid);
    if (guidedStartId && resetSucceeded && state.status === 'ready') {
      activeGuidedStartId = guidedStartId;
      captureUniversity('university_guided_start');
      // There is normally always a playlist. Avoid leaving a permanent URL
      // marker if audience configuration ever produces an empty one.
      if (state.playlist.length === 0 || !state.guided.showGuidedWelcome) {
        clearGuidedStartPending(guidedStartId);
        activeGuidedStartId = null;
      }
    }
  })().finally(() => {
    inFlight = null;
  });
}

function completedPlaylist(next: UniversityState): boolean {
  return (
    next.guided.playlistTotal > 0 &&
    next.guided.playlistWatchedCount >= next.guided.playlistTotal
  );
}

function markWatched(videoId: string): void {
  if (state.status !== 'ready') return;
  const already = Boolean(state.progress[videoId]?.watchedAt);
  if (already) return;
  const inPlaylist = state.playlist.includes(videoId);
  const next: UniversityState = {
    ...state,
    progress: {
      ...state.progress,
      [videoId]: {
        watchedAt: new Date().toISOString(),
        dismissedAt: state.progress[videoId]?.dismissedAt ?? null,
      },
    },
    guided: {
      ...state.guided,
      playlistWatchedCount:
        state.guided.playlistWatchedCount + (inPlaylist ? 1 : 0),
    },
  };
  if (completedPlaylist(next)) {
    next.guided = {...next.guided, showGettingStartedCard: false};
    if (state.guided.showGettingStartedCard) {
      captureUniversity('university_getting_started_completed', {
        playlist_total: next.guided.playlistTotal,
        video_id: videoId,
      });
    }
  }
  setState(next);
  reportVideoProgress(videoId, 'watched').catch(swallow('watched report'));
}

function dismissVideo(videoId: string): void {
  if (state.status !== 'ready') return;
  if (state.progress[videoId]?.dismissedAt) return;
  setState({
    ...state,
    progress: {
      ...state.progress,
      [videoId]: {
        watchedAt: state.progress[videoId]?.watchedAt ?? null,
        dismissedAt: new Date().toISOString(),
      },
    },
  });
  reportVideoProgress(videoId, 'dismissed').catch(swallow('dismissed report'));
}

function markWelcomeSeen(): void {
  if (!state.guided.showGuidedWelcome) return;
  setState({
    ...state,
    guided: {...state.guided, showGuidedWelcome: false},
  });
  if (activeGuidedStartId) {
    clearGuidedStartPending(activeGuidedStartId);
    activeGuidedStartId = null;
  }
  reportGuidedWelcomeSeen().catch(swallow('welcome-seen report'));
}

function acknowledgeGuidedStart(): void {
  if (!activeGuidedStartId) return;
  clearGuidedStartPending(activeGuidedStartId);
  activeGuidedStartId = null;
}

function dismissGettingStarted(): void {
  if (!state.guided.showGettingStartedCard) return;
  const watchedCount = state.guided.playlistWatchedCount;
  const playlistTotal = state.guided.playlistTotal;
  setState({
    ...state,
    guided: {...state.guided, showGettingStartedCard: false},
  });
  captureUniversity('university_getting_started_dismissed', {
    watched_count: watchedCount,
    playlist_total: playlistTotal,
    percent_complete:
      playlistTotal > 0 ? Math.round((watchedCount / playlistTotal) * 100) : 0,
  });
  reportGettingStartedDismissed().catch(swallow('card-dismissed report'));
}

export interface UseUniversityValue extends UniversityState {
  videoById: (id: string) => UniversityVideo | undefined;
  playlistVideos: UniversityVideo[];
  isWatched: (id: string) => boolean;
  markWatched: (id: string) => void;
  dismissVideo: (id: string) => void;
  markWelcomeSeen: () => void;
  acknowledgeGuidedStart: () => void;
  dismissGettingStarted: () => void;
}

export default function useUniversity(): UseUniversityValue {
  const snapshot = useSyncExternalStore(subscribe, getState, getState);

  useEffect(() => {
    ensureLoaded();
  }, []);

  const videoById = useCallback(
    (id: string) => snapshot.videos.find(video => video.id === id),
    [snapshot.videos],
  );

  const isWatched = useCallback(
    (id: string) => Boolean(snapshot.progress[id]?.watchedAt),
    [snapshot.progress],
  );

  const playlistVideos = snapshot.playlist
    .map(id => snapshot.videos.find(video => video.id === id))
    .filter(Boolean) as UniversityVideo[];

  return {
    ...snapshot,
    videoById,
    playlistVideos,
    isWatched,
    markWatched,
    dismissVideo,
    markWelcomeSeen,
    acknowledgeGuidedStart,
    dismissGettingStarted,
  };
}
